import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PayInvoiceButton } from "@/components/PayInvoiceButton";
import { PrintingCharge } from "@/components/PrintingCharge";
import { DocUploadList, PROCESS_SERVICE_DOC_TYPES, docsReady, docsMeta } from "@/components/DocUploadList";
import { Upload, FileText, X, Check, ChevronRight, ChevronLeft, ShieldCheck } from "lucide-react";

const STEPS = ["Case Details", "Defendant & Address", "Service & Add-ons", "Documents", "Review & Pay"];

export default function CreateOrder() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractNote, setExtractNote] = useState(null);
  const [autoFilled, setAutoFilled] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [reviewExtract, setReviewExtract] = useState(false);
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({
    case_number: "", jurisdiction: "", plaintiff: "", defendant: "",
    street: "", city: "", state: "", zip: "",
    service_type: "Standard", deadline: "", instructions: "",
    court_date: "",
    additional_attempts: 0, additional_defendants_same_address: 0, additional_addresses: 0,
    skip_trace: "", court_filing: false, mailing: false, stakeout_hours: 0,
    special_handling: false,
  });
  const [stInfo, setStInfo] = useState({ defendant_name: "", aliases: "", last_known_address: "", address_last_valid: "", dob: "", ssn_last4: "", phone: "", email: "", employer: "", vehicle: "", other_leads: "", prior_attempts: "" });
  const [ppAccepted, setPpAccepted] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const num = (k) => (e) => setForm({ ...form, [k]: parseInt(e.target.value || "0", 10) });

  const [terms, setTerms] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(null);
  const [agree, setAgree] = useState(false);
  const [quote, setQuote] = useState(null);
  const [agreement, setAgreement] = useState(false);
  const [svcStd, setSvcStd] = useState(null);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [printingBlocked, setPrintingBlocked] = useState(false);
  const { user } = useAuth();
  const [refundPolicy, setRefundPolicy] = useState(null);
  const [refundAgreed, setRefundAgreed] = useState(false);
  const [showRefundPolicy, setShowRefundPolicy] = useState(false);

  const reloadInvoice = async () => {
    if (!createdOrder) return;
    try { const { data } = await api.get(`/orders/${createdOrder.id}/invoice`); setCreatedInvoice(data); } catch { /* ignore */ }
  };

  useEffect(() => {
    api.get("/terms").then(({ data }) => { setTerms(data); setTermsAccepted(!!data.accepted); }).catch(() => setTermsAccepted(true));
    api.get("/service-standard").then(({ data }) => setSvcStd(data)).catch(() => {});
    api.get("/client/refund-policy").then(({ data }) => setRefundPolicy(data)).catch(() => {});
    // Detect a negotiated (Special Account) client: the backend returns the authoritative "Agreement Rate".
    api.post("/pricing/quote", { service_type: "Standard" }).then(({ data }) => {
      if (data && data.service_type === "Agreement Rate") {
        setAgreement(true);
        setForm((f) => ({ ...f, service_type: "Agreement Rate" }));
        setQuote(data);
      }
    }).catch(() => {});
  }, []);
  const acceptTerms = async () => {
    setBusy(true);
    try { await api.post("/terms/accept", { order_context: "new_order" }); setTermsAccepted(true); toast.success("Terms accepted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not accept terms"); }
    finally { setBusy(false); }
  };

  const sel = () => ({
    service_type: form.service_type,
    additional_attempts: Number(form.additional_attempts) || 0,
    additional_defendants_same_address: Number(form.additional_defendants_same_address) || 0,
    additional_addresses: Number(form.additional_addresses) || 0,
    skip_trace: form.skip_trace, court_filing: form.court_filing, mailing: form.mailing,
    stakeout_hours: Number(form.stakeout_hours) || 0, special_handling: form.special_handling,
  });

  // Recompute the quote whenever the client reaches the Review step, changes a priced selection,
  // or adds/removes a document (printing is priced from the actual uploaded pages, server-side).
  useEffect(() => {
    if (step !== 4 || createdOrder) return;
    api.post("/pricing/quote", { ...sel(), document_ids: docs.map((d) => d.id) }).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form.service_type, form.additional_attempts, form.additional_defendants_same_address,
      form.additional_addresses, form.skip_trace, form.court_filing, form.mailing, form.stakeout_hours,
      form.special_handling, docs]);

  const upload = async (e) => { e.target.value = ""; };

  const canNext = () => (step === 1 ? form.defendant && form.street && form.city && form.state : step === 3 ? docsReady(docs) : true);

  // Phase 1: PDF Document-to-Order — pre-fill the EXISTING intake fields (all remain editable).
  const extractPdf = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setExtracting(true);
    setExtractNote(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/orders/extract-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d, { id: data.id, filename: data.filename, isImage: false, isPdf: true, file: f, confirmed: false, rotation: 0, docType: "", otherDesc: "" }]); // attach like a normal upload (needs upright confirm + type)
      const ex = data.extracted || {};
      let addr = { street: "", city: "", state: "", zip: "" };
      if (ex.recipient_address) {
        const m = ex.recipient_address.match(/^(.*?),\s*([A-Za-z .]+),\s*([A-Z]{2})\s*(\d{5})/);
        addr = m ? { street: m[1].trim(), city: m[2].trim(), state: m[3], zip: m[4] } : { street: ex.recipient_address, city: "", state: "", zip: "" };
      }
      const court = ex.court_name || "";
      const note = [
        ex.document_type && `Document type: ${ex.document_type}`,
        ex.reference_number && `Reference #: ${ex.reference_number}`,
        ex.recipient_name && `Recipient: ${ex.recipient_name}`,
      ].filter(Boolean).join("\n");
      // The CURRENT extraction is the single source of truth for every auto-fillable field.
      // Supported -> populate; unsupported/ambiguous -> blank (clear any prior auto-filled value).
      const groups = [
        { keys: { case_number: ex.case_number || "" }, label: "Case number" },
        { keys: { jurisdiction: court }, label: "Court / Jurisdiction" },
        { keys: { plaintiff: ex.plaintiff || "" }, label: "Plaintiff" },
        { keys: { defendant: ex.defendant || ex.recipient_name || "" }, label: "Defendant" },
        { keys: { deadline: ex.court_date || "" }, label: "Court date" },
        { keys: { street: addr.street, city: addr.city, state: addr.state, zip: addr.zip }, label: "Service address" },
        { keys: { instructions: note || "" }, label: "Instructions", meta: true },
      ];
      const prevAuto = new Set(autoFilled);
      const next = { ...form };
      next.court_date = ex.court_date || "";
      const applied = [];
      const newAuto = [];
      groups.forEach((g) => {
        const ks = Object.keys(g.keys);
        const primary = ks[0];
        const hasCurrent = !!g.keys[primary];
        // Govern a group only if the user has NOT manually typed it, or we auto-filled it before.
        const governed = ks.every((k) => !form[k]) || ks.some((k) => prevAuto.has(k));
        if (!governed) return;
        ks.forEach((k) => { next[k] = g.keys[k]; }); // set OR clear from the current extraction
        if (hasCurrent) {
          if (!g.meta) applied.push(g.label); // metadata note doesn't count as a pre-filled intake field
          ks.forEach((k) => { if (g.keys[k]) newAuto.push(k); });
        }
      });
      setAutoFilled(newAuto);
      setForm(next);
      setReviewExtract(true);
      const n = applied.length;
      if (n > 0) {
        setExtractNote({ ok: true, n, ocr: data.ocr_used, pages: data.page_count });
        toast.success(`Pre-filled ${n} field(s) — please review & verify`);
      } else {
        setExtractNote({ ok: false, pages: data.page_count });
        toast.message("Couldn't map details to the form — please enter them manually. Your PDF is attached.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Extraction failed — please continue manually");
    } finally { setExtracting(false); }
  };

  const submit = async () => {
    if (!docsReady(docs)) { toast.error("Confirm each image is upright and choose a document type for every file."); return; }
    setBusy(true);
    try {
      const payload = {
        case_number: form.case_number, jurisdiction: form.jurisdiction, plaintiff: form.plaintiff,
        defendant: form.defendant,
        service_address: { street: form.street, city: form.city, state: form.state, zip: form.zip },
        deadline: form.deadline, instructions: form.instructions,
        court_date: form.court_date || "",
        documents: docs.map((d) => d.id), documents_meta: docsMeta(docs), contacts: [], ...sel(),
        skip_trace_info: stInfo, permissible_purpose_accepted: ppAccepted,
        permissible_purpose_text: "I certify this search is for service of process in a legal proceeding (permissible purpose).",
      };
      const { data } = await api.post("/orders", payload);
      setCreatedOrder(data);
      if (refundPolicy?.checkbox_required && refundAgreed) {
        try { await api.post(`/orders/${data.id}/refund-policy/accept`, { legal_name: user?.name || "" }); } catch { /* non-blocking */ }
      }
      if (data.status === "awaiting_approval") {
        toast.success(`Service request #${data.job_number} submitted for ARPS approval`);
      } else if (data.payment_required) {
        toast.success(`Order #${data.job_number} created`);
        try { const { data: inv } = await api.get(`/orders/${data.id}/invoice`); setCreatedInvoice(inv); }
        catch { /* payment panel will show generic message */ }
      } else {
        toast.success(`Order #${data.job_number} created`);
        nav(`/client/orders/${data.id}/confirmation`, { state: { order: data, docs } });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create order");
    } finally { setBusy(false); }
  };

  if (termsAccepted === null) {
    return <div className="mx-auto max-w-3xl py-16 text-center text-slate-400" data-testid="terms-loading">Loading…</div>;
  }

  // ----- Awaiting-approval panel (NEW CLIENT: request entered before admin approval; not charged) -----
  if (createdOrder && createdOrder.status === "awaiting_approval") {
    return (
      <div className="mx-auto max-w-2xl space-y-6" data-testid="order-awaiting-approval-panel">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Service request submitted</h2>
          <p className="text-slate-500">Request <strong>#{createdOrder.job_number}</strong> is complete and under <strong>ARPS review</strong>. Your card will <strong>not</strong> be charged yet.</p>
        </div>
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 text-sm text-indigo-800">
          <p>Once ARPS approves your account, you'll receive an email and a single <strong>Pay</strong> action becomes available. After payment is confirmed, your assignment enters the ARPS service queue.</p>
        </div>
        {createdOrder.pricing && <div className="rounded-xl border border-slate-200 bg-white p-6"><QuoteBreakdown quote={createdOrder.pricing} /></div>}
        <Button variant="outline" data-testid="go-to-request" onClick={() => nav(`/client/orders/${createdOrder.id}`)}>View request</Button>
      </div>
    );
  }

  // ----- Payment panel (after a standard order is created; server gate releases it only on confirmed capture) -----
  if (createdOrder && createdOrder.payment_required) {
    return (
      <div className="mx-auto max-w-2xl space-y-6" data-testid="order-payment-panel">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Payment required</h2>
          <p className="text-slate-500">Order <strong>#{createdOrder.job_number}</strong> is created and <strong>awaiting payment</strong>. It will only be released to a process server after your payment is confirmed.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <QuoteBreakdown quote={createdOrder.pricing} />
          <div className="mt-4"><PrintingCharge orderId={createdOrder.id} role="client" onStatus={setPrintingBlocked} onChanged={reloadInvoice} /></div>
          <p className="mt-4 text-xs text-slate-500" data-testid="checkout-timeframe-note">Timeframes start when complete, ready-to-serve documents and payment are received. Business days are Monday–Friday, excluding holidays.</p>
          {createdInvoice ? (
            <>
              {printingBlocked ? (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-700" data-testid="pay-blocked-note">Confirm the document printing charge above to enable payment.</p>
              ) : (
                <PayInvoiceButton invoice={createdInvoice} onPaid={() => { toast.success("Payment confirmed — order released to our servers."); nav(`/client/orders/${createdOrder.id}/confirmation`, { state: { order: createdOrder } }); }} />
              )}
              <p className="mt-3 text-center text-xs text-slate-400">Your order stays out of the server pool until your payment is confirmed and ARPS verifies the order.</p>
            </>
          ) : (
            <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700" data-testid="pay-later-note">
              Invoice <strong>{createdOrder.job_number}</strong> generated. You can pay it from your invoices. The order stays unassigned until payment is confirmed.
            </p>
          )}
        </div>
        <Button variant="outline" data-testid="go-to-order" onClick={() => nav(`/client/orders/${createdOrder.id}/confirmation`, { state: { order: createdOrder } })}>View order</Button>
      </div>
    );
  }

  if (!termsAccepted) {
    return (
      <div className="mx-auto max-w-3xl space-y-6" data-testid="terms-gate">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Accept ARPS Service Terms</h2>
          <p className="text-slate-500">You must accept the current terms ({terms?.version}) before submitting work.</p>
        </div>
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-6">
          {terms?.sections && terms.sections.length ? (
            <div className="space-y-4">
              {terms.sections.map((sec, si) => (
                <div key={si} data-testid={`terms-section-${si}`}>
                  <h3 className="font-display text-base font-bold text-slate-900" data-testid={`terms-section-heading-${si}`}>{sec.heading}</h3>
                  <ul className="mt-1.5 list-disc space-y-1.5 pl-5 text-sm text-slate-600">
                    {(sec.paragraphs || []).map((p, pi) => <li key={pi}>{p}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="list-disc space-y-2 pl-5 text-sm text-slate-600">
              {(terms?.content || []).map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          )}
          <label className="flex items-start gap-2 pt-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="terms-agree-checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" />
            <span>I have read and agree to the ARPS Service Terms ({terms?.version}).</span>
          </label>
          <Button data-testid="terms-accept-button" disabled={!agree || busy} onClick={acceptTerms} className="bg-slate-900 text-white hover:bg-slate-800">Accept &amp; continue</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Start a Service</h2>
        <p className="text-slate-500">Select your service, review the full price, accept the current terms, and pay to release your order.</p>
      </div>

      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-2">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span className={`hidden text-xs font-medium sm:block ${i === step ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="h-px flex-1 bg-slate-200" />}
          </React.Fragment>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        {step === 0 && (
          <div className="space-y-4">
            <div className="rounded-xl border border-dashed border-emerald-300 bg-emerald-50/50 p-4" data-testid="pdf-autofill-card">
              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-emerald-600" /><p className="text-sm font-semibold text-slate-800">Auto-fill from a PDF (optional)</p></div>
              <p className="mt-1 text-xs text-slate-500">Upload a summons, subpoena or complaint and we'll try to pre-fill the fields below. The manual form still works exactly as before.</p>
              <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700" data-testid="pdf-autofill-btn">
                <Upload className="h-4 w-4" /> {extracting ? "Reading PDF…" : "Upload PDF to auto-fill"}
                <input type="file" accept=".pdf" className="hidden" disabled={extracting} onChange={extractPdf} data-testid="pdf-autofill-input" />
              </label>
              {extractNote && (extractNote.ok
                ? <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="pdf-autofill-verify">Pre-filled {extractNote.n} field(s){extractNote.ocr ? " via OCR" : ""} ({extractNote.pages} page PDF). ⚠️ Please review and verify every field before submitting — extracted data is not guaranteed accurate.</p>
                : <p className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500" data-testid="pdf-autofill-fallback">We couldn't extract details from this PDF{extractNote.pages ? ` (${extractNote.pages} pages)` : ""}. Your document is attached — please enter the details manually.</p>)}
            </div>
            {reviewExtract && (
              <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-4" data-testid="ocr-review-panel">
                <p className="text-sm font-semibold text-slate-800">Review the details we found</p>
                <p className="mt-1 text-xs text-slate-600">These are pre-filled from your document — please check each one and correct anything that's wrong. If we didn't find a value, it's left blank; fill it in if you have it, or leave it empty and continue.</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div><Label>Case number</Label><Input data-testid="ocr-review-case-number" value={form.case_number} onChange={set("case_number")} placeholder="Not found — add if known" /></div>
                  <div><Label>Court / Jurisdiction</Label><Input data-testid="ocr-review-jurisdiction" value={form.jurisdiction} onChange={set("jurisdiction")} placeholder="Not found — add if known" /></div>
                  <div><Label>Plaintiff / Petitioner</Label><Input data-testid="ocr-review-plaintiff" value={form.plaintiff} onChange={set("plaintiff")} placeholder="Not found — add if known" /></div>
                  <div><Label>Defendant / Respondent</Label><Input data-testid="ocr-review-defendant" value={form.defendant} onChange={set("defendant")} placeholder="Not found — add if known" /></div>
                  <div className="sm:col-span-2"><Label>Street address</Label><Input data-testid="ocr-review-street" value={form.street} onChange={set("street")} placeholder="Not found — add if known" /></div>
                  <div><Label>City</Label><Input data-testid="ocr-review-city" value={form.city} onChange={set("city")} placeholder="Not found" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>State</Label><Input data-testid="ocr-review-state" value={form.state} onChange={set("state")} placeholder="ST" /></div>
                    <div><Label>ZIP</Label><Input data-testid="ocr-review-zip" value={form.zip} onChange={set("zip")} placeholder="ZIP" /></div>
                  </div>
                  <div><Label>Court date / Deadline</Label><Input data-testid="ocr-review-deadline" type="date" value={form.deadline} onChange={set("deadline")} /></div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <Button data-testid="ocr-confirm-btn" onClick={() => { setReviewExtract(false); toast.success("Details confirmed"); }} className="bg-emerald-600 text-white hover:bg-emerald-700">
                    <Check className="mr-1 h-4 w-4" /> Confirm details &amp; continue
                  </Button>
                  <span className="text-xs text-slate-500">You can leave fields blank — nothing here is required to continue.</span>
                </div>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Case number</Label><Input data-testid="input-case-number" value={form.case_number} onChange={set("case_number")} placeholder="CV-2026-01847" /></div>
              <div><Label>Court / Jurisdiction</Label><Input data-testid="input-jurisdiction" value={form.jurisdiction} onChange={set("jurisdiction")} placeholder="Shelby County Circuit Court" /></div>
            </div>
            <div><Label>Plaintiff / Petitioner</Label><Input data-testid="input-plaintiff" value={form.plaintiff} onChange={set("plaintiff")} placeholder="Acme Corp." /></div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div><Label>Defendant / Respondent *</Label><Input data-testid="input-defendant" value={form.defendant} onChange={set("defendant")} placeholder="John Doe" /></div>
            <div><Label>Street address *</Label><Input data-testid="input-street" value={form.street} onChange={set("street")} placeholder="123 Main St, Apt 4" /></div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div><Label>City *</Label><Input data-testid="input-city" value={form.city} onChange={set("city")} placeholder="Memphis" /></div>
              <div><Label>State *</Label><Input data-testid="input-state" value={form.state} onChange={set("state")} placeholder="TN" /></div>
              <div><Label>ZIP</Label><Input data-testid="input-zip" value={form.zip} onChange={set("zip")} placeholder="38141" /></div>
            </div>
          </div>
        )}

        {step === 2 && (agreement ? (
          <div className="space-y-4">
            <div data-testid="agreement-rate-card" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Service type</p>
                  <p className="font-display text-lg font-bold text-slate-900" data-testid="agreement-service-type">Agreement Rate</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium text-slate-500">Your negotiated rate</p>
                  <p className="text-xl font-extrabold text-emerald-700" data-testid="agreement-amount">{quote ? fmtMoney(quote.currency || "USD", quote.base_price) : "—"}</p>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-600">Your account has an active negotiated Agreement Rate configured by ARPS. This amount is applied automatically — no service selection or add-ons are needed.</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="printing-auto-note-agreement">Document printing is calculated automatically from the pages of the documents you upload, at your negotiated per-page rate. You'll review and confirm the exact charge before payment.</div>
            <div><Label>Deadline</Label><Input data-testid="input-deadline" type="date" value={form.deadline} onChange={set("deadline")} /></div>
            <div><Label>Special instructions</Label><Textarea data-testid="input-instructions" value={form.instructions} onChange={set("instructions")} rows={3} placeholder="Servee age, vehicle/plate, phone numbers, gate codes, work hours" />{form.instructions && <p data-testid="instructions-hint" className="mt-1 text-xs text-slate-500">Servee age, vehicle/plate, phone numbers, gate codes, work hours</p>}</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Service type</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger data-testid="select-service-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard">Routine Process Service — $95</SelectItem>
                  <SelectItem value="Rush">Rush Process Service — $135</SelectItem>
                  <SelectItem value="24-Hour Priority">24-Hour Priority Service — $285</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-slate-400">Up to 4 documented attempts included. No guaranteed successful service.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Additional attempts ($25 ea)</Label><Input data-testid="addon-additional-attempts" type="number" min="0" value={form.additional_attempts} onChange={num("additional_attempts")} /></div>
              <div><Label>Additional defendant, same address ($35 ea)</Label><Input data-testid="addon-additional-defendants" type="number" min="0" value={form.additional_defendants_same_address} onChange={num("additional_defendants_same_address")} /></div>
              <div><Label>Additional address ($50 ea)</Label><Input data-testid="addon-additional-addresses" type="number" min="0" value={form.additional_addresses} onChange={num("additional_addresses")} /></div>
              <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="printing-auto-note">Document printing is calculated automatically from the pages of the documents you upload, at $0.15/page for the first 25 pages and $0.10/page after that (or your negotiated rate). The exact charge is shown in your total at Review &amp; Pay.</div>
              <div><Label>Stakeout hours ($100/hr, 2-hr min)</Label><Input data-testid="addon-stakeout-hours" type="number" min="0" value={form.stakeout_hours} onChange={num("stakeout_hours")} /></div>
              <div>
                <Label>Skip trace</Label>
                <Select value={form.skip_trace || "none"} onValueChange={(v) => setForm({ ...form, skip_trace: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="addon-skip-trace"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="basic">Basic skip trace — $49</SelectItem>
                    <SelectItem value="advanced">Advanced skip trace — $75</SelectItem>
                  </SelectContent>
                </Select>
                <div className="mt-1.5 space-y-0.5 text-xs text-slate-500" data-testid="skip-trace-turnaround-note">
                  <p>Basic: Target turnaround: within 24 business hours after payment and complete information.</p>
                  <p>Advanced: Target turnaround: within 48-72 business hours after payment and complete information.</p>
                  <p>Turnaround times are targets, not guarantees. The clock starts when payment is received and the required defendant information is complete.</p>
                </div>
              </div>
            </div>
            {(form.skip_trace === "basic" || form.skip_trace === "advanced") && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 space-y-3" data-testid="skip-trace-section">
                <p className="text-sm font-semibold text-slate-800">Skip Trace Information</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><Label>Defendant full legal name *</Label><Input data-testid="st-name" value={stInfo.defendant_name || form.defendant} onChange={(e) => setStInfo({ ...stInfo, defendant_name: e.target.value })} /></div>
                  <div><Label>Aliases / maiden name</Label><Input data-testid="st-aliases" value={stInfo.aliases} onChange={(e) => setStInfo({ ...stInfo, aliases: e.target.value })} /></div>
                  <div className="sm:col-span-2"><Label>Last known address *</Label><Input data-testid="st-address" value={stInfo.last_known_address} onChange={(e) => setStInfo({ ...stInfo, last_known_address: e.target.value })} /></div>
                  <div><Label>Approx. date last valid</Label><Input data-testid="st-address-date" value={stInfo.address_last_valid} onChange={(e) => setStInfo({ ...stInfo, address_last_valid: e.target.value })} placeholder="e.g. 2023" /></div>
                  <div><Label>DOB</Label><Input data-testid="st-dob" value={stInfo.dob} onChange={(e) => setStInfo({ ...stInfo, dob: e.target.value })} /></div>
                  <div><Label>Last 4 of SSN</Label><Input data-testid="st-ssn4" maxLength={4} value={stInfo.ssn_last4} onChange={(e) => setStInfo({ ...stInfo, ssn_last4: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input data-testid="st-phone" value={stInfo.phone} onChange={(e) => setStInfo({ ...stInfo, phone: e.target.value })} /></div>
                  <div><Label>Email</Label><Input data-testid="st-email" value={stInfo.email} onChange={(e) => setStInfo({ ...stInfo, email: e.target.value })} /></div>
                  <div><Label>Employer</Label><Input data-testid="st-employer" value={stInfo.employer} onChange={(e) => setStInfo({ ...stInfo, employer: e.target.value })} /></div>
                  <div><Label>Vehicle</Label><Input data-testid="st-vehicle" value={stInfo.vehicle} onChange={(e) => setStInfo({ ...stInfo, vehicle: e.target.value })} /></div>
                  <div className="sm:col-span-2"><Label>Other leads / notes</Label><Textarea data-testid="st-leads" rows={2} value={stInfo.other_leads} onChange={(e) => setStInfo({ ...stInfo, other_leads: e.target.value })} /></div>
                  <div className="sm:col-span-2"><Label>Prior failed attempts</Label><Textarea data-testid="st-prior" rows={2} value={stInfo.prior_attempts} onChange={(e) => setStInfo({ ...stInfo, prior_attempts: e.target.value })} /></div>
                </div>
                <p className="text-xs text-slate-500">Required: name and last known address. Everything else is optional.</p>
                <div className="space-y-1 rounded-md border border-amber-200 bg-white/60 p-2.5 text-xs text-slate-600" data-testid="skip-trace-policy-note">
                  <p>{form.skip_trace === "basic" ? "Target turnaround: within 24 business hours after payment and complete information." : "Target turnaround: within 48-72 business hours after payment and complete information."}</p>
                  <p>Turnaround times are targets, not guarantees. The clock starts when payment is received and the required defendant information is complete.</p>
                  <p>Skip trace fees cover the search performed. If a search returns no new address or contact information, the fee is still owed. Results depend on available data and are not guaranteed.</p>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-4 pt-1">
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="addon-court-filing" checked={form.court_filing} onChange={(e) => setForm({ ...form, court_filing: e.target.checked })} /> Court filing ($50 + actual court fees)</label>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="addon-mailing" checked={form.mailing} onChange={(e) => setForm({ ...form, mailing: e.target.checked })} /> Mailing/shipping (actual + handling)</label>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="addon-special-handling" checked={form.special_handling} onChange={(e) => setForm({ ...form, special_handling: e.target.checked })} /> Special handling ($50)</label>
            </div>
            <div><Label>Deadline</Label><Input data-testid="input-deadline" type="date" value={form.deadline} onChange={set("deadline")} /></div>
            <div><Label>Special instructions</Label><Textarea data-testid="input-instructions" value={form.instructions} onChange={set("instructions")} rows={3} placeholder="Servee age, vehicle/plate, phone numbers, gate codes, work hours" />{form.instructions && <p data-testid="instructions-hint" className="mt-1 text-xs text-slate-500">Servee age, vehicle/plate, phone numbers, gate codes, work hours</p>}</div>
          </div>
        ))}

        {step === 3 && (
          <div className="space-y-4">
            <DocUploadList docs={docs} setDocs={setDocs} docTypes={PROCESS_SERVICE_DOC_TYPES} accent="emerald" />
            {docs.length > 0 && !docsReady(docs) && (
              <p data-testid="docs-incomplete-hint" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">Each file needs a document type, and every image must be confirmed upright, before you can submit.</p>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="review-pay-step">
            <h3 className="font-display text-lg font-bold text-slate-900">Review &amp; pay</h3>
            {!docsReady(docs) && (
              <div data-testid="review-docs-uncheck" className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <span>Your documents need a quick check before payment.</span>
                <Button type="button" size="sm" variant="outline" data-testid="review-back-to-docs" onClick={() => setStep(3)}>Review documents</Button>
              </div>
            )}
            {(!form.defendant || !form.street || !form.city || !form.state) ? (
              <div data-testid="review-missing-required" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">Complete defendant name and service address before submitting.</div>
            ) : (
              <QuoteBreakdown quote={quote} />
            )}
            {svcStd && (
              <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600" data-testid="service-standard-disclosure">
                <p className="font-semibold text-slate-700">ARPS Standard Service — up to {svcStd.attempts} diligent attempts</p>
                <p className="mt-1">Attempts are scheduled within these authorized windows (scheduling windows, not a minimum on-site time):</p>
                <ul className="mt-1.5 space-y-0.5">
                  {(svcStd.windows || []).map((w) => (
                    <li key={w.key} data-testid={`svc-window-${w.key}`} className="flex justify-between gap-4">
                      <span className="text-slate-500">{w.label}</span>
                      <span className="font-medium text-slate-800">{w.display}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-slate-500">Completed diligent service is fully billable even if unsuccessful. Skip trace, alternate addresses, and additional attempts require separate authorization and fees.</p>
              </div>
            )}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="cancellation-terms">
              <p className="font-semibold text-slate-700">Cancellation Policy (summary)</p>
              <p className="mt-1">Cancel free before work begins. After server acceptance, Accept &amp; Print, or an attempt, applicable fees, earned server compensation, and authorized expenses remain payable. Successful service is not cancelled retroactively. Full terms are in ARPS Terms &amp; Conditions ({terms?.version}).</p>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="terms-accepted-badge">
              <ShieldCheck className="h-4 w-4" /> You have accepted the current ARPS Terms ({terms?.version}).
            </div>
            <label data-testid="confirm-submit-checkbox" className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4" />
            </label>
            {(form.skip_trace === "basic" || form.skip_trace === "advanced") && (
              <>
                <label data-testid="permissible-purpose-checkbox" className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm">
                  <input type="checkbox" checked={ppAccepted} onChange={(e) => setPpAccepted(e.target.checked)} className="mt-0.5 h-4 w-4" />
                  <span className="text-slate-700">I certify this search is for service of process in a legal proceeding (permissible purpose).</span>
                </label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="skip-trace-noresult-ack">Skip trace fees cover the search performed. If a search returns no new address or contact information, the fee is still owed. Results depend on available data and are not guaranteed.</div>
              </>
            )}
            {refundPolicy && (refundPolicy.checkbox_required ? (
              <label data-testid="refund-policy-checkbox" className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm">
                <input type="checkbox" checked={refundAgreed} onChange={(e) => setRefundAgreed(e.target.checked)} className="mt-0.5 h-4 w-4" />
                <span className="text-slate-700">I have read and agree to the{" "}
                  {refundPolicy.scope === "arps" ? (
                    <button type="button" data-testid="view-refund-policy" onClick={() => setShowRefundPolicy(true)} className="font-semibold text-emerald-600 underline">{refundPolicy.title}</button>
                  ) : (
                    <a data-testid="view-refund-policy" href={refundPolicy.policy_url} target="_blank" rel="noreferrer" className="font-semibold text-emerald-600 underline">{refundPolicy.title}</a>
                  )}.
                </span>
              </label>
            ) : (
              <div data-testid="refund-policy-placeholder" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">{refundPolicy.placeholder}</div>
            ))}
            {showRefundPolicy && refundPolicy?.content && (
              <div data-testid="refund-policy-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowRefundPolicy(false)}>
                <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between border-b border-slate-200 p-4">
                    <h3 className="font-display text-lg font-bold text-slate-900">{refundPolicy.title}</h3>
                    <button data-testid="close-refund-policy" onClick={() => setShowRefundPolicy(false)} className="text-slate-400 hover:text-slate-700">✕</button>
                  </div>
                  {!refundPolicy.legally_final && <p className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700">Draft — final wording pending.</p>}
                  <pre className="flex-1 overflow-auto whitespace-pre-wrap p-4 font-sans text-xs leading-relaxed text-slate-700">{refundPolicy.content}</pre>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-between">
          <Button data-testid="wizard-back" variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button data-testid="wizard-next" disabled={!canNext()} onClick={() => setStep(step + 1)} className="bg-slate-900 text-white hover:bg-slate-800">
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button data-testid="submit-order-button" disabled={busy || !confirmed || !docsReady(docs) || !form.defendant || !form.street || !form.city || !form.state || (refundPolicy?.checkbox_required && !refundAgreed) || ((form.skip_trace === "basic" || form.skip_trace === "advanced") && (!ppAccepted || !stInfo.last_known_address.trim()))} onClick={submit} className="bg-emerald-600 text-white hover:bg-emerald-700">
              {busy ? "Submitting…" : "Submit & pay"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function QuoteBreakdown({ quote }) {
  if (!quote) return <div className="text-sm text-slate-400" data-testid="quote-loading">Calculating price…</div>;
  const cur = quote.currency || "USD";
  const printing = quote.printing || {};
  const printDocs = printing.documents || [];
  // Printing is rendered as its own itemized block (below), so keep it out of the generic line list.
  const otherLines = (quote.line_items || []).filter((li) => li.code !== "printing");
  return (
    <div className="space-y-2" data-testid="quote-breakdown">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-slate-900">{quote.service_label} ({quote.service_type})</span>
        <span data-testid="quote-base-price" className="font-semibold text-slate-900">{fmtMoney(cur, quote.base_price)}</span>
      </div>
      {quote.timeframe ? <p className="text-xs text-slate-500">Included attempts: {quote.included_attempts} · {quote.timeframe}</p> : null}
      <div className="divide-y divide-slate-100 border-y border-slate-100">
        {otherLines.map((li, i) => (
          <div key={i} className="flex justify-between py-1.5 text-sm">
            <span className="text-slate-600">{li.description}{li.note ? <span className="block text-xs text-slate-400">{li.note}</span> : null}</span>
            <span className="font-medium text-slate-800">{fmtMoney(cur, li.amount)}</span>
          </div>
        ))}
        {printDocs.length > 0 ? (
          <div className="py-1.5 text-sm" data-testid="printing-breakdown">
            <div className="font-medium text-slate-700">Printing</div>
            <div className="mt-1 space-y-0.5 pl-3">
              {printDocs.map((d, i) => (
                <div key={i} className="flex justify-between gap-3" data-testid={`printing-doc-${i}`}>
                  <span className="truncate text-slate-600">{d.name}</span>
                  <span className={d.resolved ? "shrink-0 text-slate-700" : "shrink-0 font-medium text-amber-600"}>
                    {d.resolved ? `${d.pages} ${d.pages === 1 ? "page" : "pages"}` : "pending ARPS count"}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-1 flex justify-between border-t border-slate-100 pt-1 pl-3 font-medium text-slate-800">
              <span data-testid="printing-total-pages">Total: {printing.pages || 0} {(printing.pages || 0) === 1 ? "page" : "pages"}{printing.resolved === false ? " + pending" : ""}</span>
              <span data-testid="printing-total-amount">{fmtMoney(cur, printing.amount || 0)}</span>
            </div>
          </div>
        ) : null}
      </div>
      {printing.resolved === false ? (
        <p className="text-xs text-amber-600" data-testid="printing-pending-note">Some documents are pending an ARPS page count. Your total updates and payment opens once every page is counted.</p>
      ) : null}
      <div className="flex items-center justify-between pt-1">
        <span className="font-bold text-slate-900">Total due</span>
        <span data-testid="quote-total-due" className="text-lg font-extrabold text-emerald-700">{fmtMoney(cur, quote.total_due)}</span>
      </div>
    </div>
  );
}
